import type { EmbedReference } from '@shared/ast/astTypes'
import { isEmbedReference } from '@shared/ast/embedReference'
import {
  $applyNodeReplacement,
  type BaseSelection,
  type DOMExportOutput,
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical'

import { $isCustomInlineEmbedNode } from './CustomInlineEmbedNode'
import { SourceEmbedView } from '../SourceEmbedView'

export type SerializedCustomEmbedNode = Spread<{ blockId: string; source: EmbedReference }, SerializedLexicalNode>

/** Only the stable source reference belongs to editor state; fetched prose never does. */
export class CustomEmbedNode extends DecoratorNode<JSX.Element> {
  __blockId: string
  __source: EmbedReference

  static getType(): string {
    return 'custom-embed'
  }

  static clone(node: CustomEmbedNode): CustomEmbedNode {
    return new CustomEmbedNode(node.__blockId, node.__source, node.__key)
  }

  constructor(blockId: string, source: EmbedReference, key?: NodeKey) {
    super(key)
    if (!isEmbedReference(source)) {
      throw new Error('Invalid embed reference')
    }
    this.__blockId = blockId
    this.__source = { ...source }
  }

  getBlockId(): string {
    return this.__blockId
  }

  getSource(): EmbedReference {
    return { ...this.getLatest().__source }
  }

  setSource(source: EmbedReference): this {
    if (!isEmbedReference(source)) {
      throw new Error('Invalid embed reference')
    }
    this.getWritable().__source = { ...source }
    return this
  }

  setBlockId(blockId: string): this {
    this.getWritable().__blockId = blockId
    return this
  }

  createDOM(): HTMLElement {
    const element = document.createElement('div')
    element.setAttribute('data-block-id', this.__blockId)
    element.setAttribute('data-source-embed', 'true')
    element.contentEditable = 'false'
    return element
  }

  updateDOM(prevNode: CustomEmbedNode, dom: HTMLElement): boolean {
    if (prevNode.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    return false
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div')
    element.setAttribute('data-source-embed', JSON.stringify(this.__source))
    element.textContent = `↪ ${this.__source.filename}`
    return { element }
  }

  static importJSON(node: SerializedCustomEmbedNode): CustomEmbedNode {
    return $createCustomEmbedNode(node.blockId, node.source)
  }

  exportJSON(): SerializedCustomEmbedNode {
    return { type: 'custom-embed', version: 1, blockId: this.__blockId, source: { ...this.__source } }
  }

  isInline(): boolean {
    return false
  }

  getTextContent(): string {
    return `↪ ${this.__source.filename}`
  }

  decorate(): JSX.Element {
    return <SourceEmbedView source={this.__source} nodeKey={this.__key} />
  }
}

export function $createCustomEmbedNode(blockId: string, source: EmbedReference): CustomEmbedNode {
  return $applyNodeReplacement(new CustomEmbedNode(blockId, source))
}

export function $isCustomEmbedNode(node: LexicalNode | null | undefined): node is CustomEmbedNode {
  return node instanceof CustomEmbedNode
}

export function $selectionContainsEmbed(selection: BaseSelection | null): boolean {
  return Boolean(selection?.getNodes().some((node) => $isCustomEmbedNode(node) || $isCustomInlineEmbedNode(node)))
}
